import { Form, Input, Button, Space } from 'antd';
import { useEffect } from 'react';
import api from '../utils/apiClient';

const UserForm = ({ user, isAdmin = false, persist = true, tenantId, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const isEdit = !!user && user.user_id;
  const isRootTenant = tenantId === 'scatr';

  useEffect(() => {
    if (isEdit) {
      form.setFieldsValue(user);
    } else {
      !!user ? form.setFieldsValue(user) : form.resetFields();
    }
  }, [user, isEdit, form]);

  const handleFinish = async (tenantId, values) => {
    try {
      if (persist && isEdit) {
        const { data } = await api.put(`/tenants/${tenantId}/users/${user.user_id}`, values);
        onSuccess?.(data);
      } else if (persist && !isEdit) {
        const { data } = await api.post(`/tenants/${tenantId}/users`, values);
        onSuccess?.(data);
      } else {
        // just return user object back to caller
        const newUser = isEdit
          ? { ...user, ...values }
          : { user_id: crypto.randomUUID(), ...values }; // user_id is unused. Just allowing React to calm down with proper unique key
        onSuccess?.(newUser);
      }
    } catch (err) {
      console.error('Error saving user', err);
    }
  };

  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 6 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 14 },
    },
  };

  return (
    <Form
      form={form}
      {...formItemLayout}
      layout="horizontal"
      variant="outlined"
      onFinish={(value) => handleFinish(tenantId, value)}
    >
      <Form.Item
        name="username"
        label="Username"
        rules={[
            { required: true, message: 'Username is required' },
            {
                validator: (() => {
                    let timer;
                    let lastReject;
                    const DEBOUNCE_REQUEST_MS = 400;

                    return async (_, username) => {
                        if (!username || isEdit) return;

                        // cancel previous debounce
                        if (lastReject) {
                            lastReject();
                            lastReject = null;
                        }
                        if (timer) {
                            clearTimeout(timer);
                            timer = null;
                        }

                        return new Promise((resolve, reject) => {
                            lastReject = () => reject();

                            timer = setTimeout(async () => {
                                try {
                                    const { data: { taken } } = await api.post(
                                        '/users/is-name-available',
                                        { username }
                                    );
                                    if (taken) {
                                        reject(new Error('This username is already taken'));
                                    } else {
                                        resolve();
                                    }
                                } catch (err) {
                                    console.error('Username check failed', err);
                                    resolve();
                                } finally {
                                    lastReject = null;
                                }
                            }, DEBOUNCE_REQUEST_MS);
                        });
                    };
                })(),
            }
        ]}
        hasFeedback
      >
        <Input disabled={!!user?.user_id} />
      </Form.Item>

      <Form.Item name="first_name" label="First Name">
        <Input disabled={isRootTenant} />
      </Form.Item>

      <Form.Item name="last_name" label="Last Name">
        <Input disabled={isRootTenant} />
      </Form.Item>

      <Form.Item name="email" label="Email">
        <Input disabled={isRootTenant} type="email" />
      </Form.Item>

      <Form.Item name="phone" label="Phone">
        <Input disabled={isRootTenant} />
      </Form.Item>

      {!isEdit && (
        <Form.Item
          name="temporary_password"
          label="Temporary Password"
          rules={[{ required: true, message: 'Temporary password is required' }]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
      )}

      {!isAdmin && ( // Admin cannot have separate organization. It's org is the tenant itself
        <Form.Item name="organization" label="Organization">
          <Input disabled={isRootTenant} />
        </Form.Item>
      )}

      <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {onCancel && <Button onClick={onCancel}>Cancel</Button>}
        <Button type="primary" htmlType="submit">
          {isEdit ? 'Update' : 'Create'}
        </Button>
      </Space>
    </Form>
  );
};

export default UserForm;
